import React, { useEffect, useState } from "react";
import { getCallStatsByOrgId } from "../../utils/api/callAPI";
import { useParams } from "react-router-dom";
import { format, subDays } from "date-fns";
import DatePicker from "react-datepicker";


import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const UnitCountSeven = ({ data }) => {
  const { id } = useParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("day");
  const [dateFilter, setDateFilter] = useState("last7");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const now = new Date();
      let startDate, endDate;
      let dynamicStep = "day";

      if (dateFilter === "last7") {
        endDate = now;
        startDate = subDays(new Date(now), 6);
        dynamicStep = "day";
      } else if (dateFilter === "last30") {
        endDate = now;
        startDate = subDays(new Date(now), 29); 
        dynamicStep = "week";
      } else if (dateFilter === "custom" && customStartDate && customEndDate) {
        startDate = customStartDate;
        endDate = customEndDate;

        const diffInDays = Math.ceil(
          (customEndDate - customStartDate) / (1000 * 60 * 60 * 24)
        );

        if (diffInDays <= 7) dynamicStep = "day";
        else if (diffInDays <= 30) dynamicStep = "week";
        else dynamicStep = "month";
      } else {
        setLoading(false);
        return;
      }

      const formattedStart = format(startDate, "yyyy-MM-dd");
      const formattedEnd = format(endDate, "yyyy-MM-dd");

      try {
        const res = await getCallStatsByOrgId({
          startDate: formattedStart,
          endDate: formattedEnd,
          step: dynamicStep,
          organizationId: id,
        });

        if (res) {
          setSummary(res);
          setStep(dynamicStep);
        }
      } catch (error) {
        console.error("Error fetching call stats:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchData();
  }, [id, dateFilter, customStartDate, customEndDate]);
  if (loading) {
    return (
      <div className="container mt-4 mb-5">
        <div className="row g-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="col-md-6">
              <div
                className="p-3 rounded bg-white shadow-sm"
                style={{ minHeight: "260px" }}
              >
                <div className="mb-3 shimmer shimmer-text"></div>
                <div className="mb-2 shimmer shimmer-id"></div>
                <div className="w-100 shimmer" style={{ height: "180px", borderRadius: "12px" }}></div>
              </div>
            </div>
          ))}

          <div className="col-12">
            <div
              className="p-3 rounded bg-white shadow-sm"
              style={{ minHeight: "300px" }}
            >
              <div className="mb-3 shimmer shimmer-text"></div>
              <div className="mb-2 shimmer shimmer-id"></div>
              <div className="w-100 shimmer" style={{ height: "200px", borderRadius: "12px" }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!summary) return <div className="text-danger">Failed to fetch data.</div>;

  const labels = summary.stepData.map((d) => d.step);
  const callCounts = summary.stepData.map((d) => d.callCount);
  const minutes = summary.stepData.map((d) => d.minutes.toFixed(2));

  const callChartData = {
    labels,
    datasets: [
      {
        label: "Total Calls",
        data: callCounts,
        borderColor: "rgba(54, 162, 235, 0.7)",
        backgroundColor: "rgba(54, 162, 235, 0.7)",
        tension: 0.4,
        fill: false,
      },
    ],
  };

  const minutesChartData = {
    labels,
    datasets: [
      {
        label: "Total Minutes",
        data: minutes,
        borderColor: "rgba(75, 192, 192, 1)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        tension: 0.4,
        fill: false,
      },
    ],
  };

  const reasonsLabels = summary.endedReasonStats.map((r) => r.reason);
  const reasonsData = summary.endedReasonStats.map((r) => r.count);

  const reasonsChartData = {
    labels: reasonsLabels,
    datasets: [
      {
        label: "Call End Reasons",
        data: reasonsData,
        backgroundColor: [
          "#ec4899",
          "#3b82f6",
          "#facc15",
          "#8b5cf6",
          "#10b981",
        ],
        borderRadius: 6,
        barThickness: 30,
      },
    ],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.raw} Calls`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: 12,
          },
          callback: function (value, index, ticks) {
            const label = this.getLabelForValue(value);
            return label.length > 10 ? label.slice(0, 10) + "..." : label;
          },
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 10,
          font: {
            size: 12,
          },
        },
      },
    },
  };

  return (
    <div className="mt-4 mb-5 px-3">
      <div className="d-flex mb-10 justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div className="fw-bold">Call Summary</div>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          <label className="mb-2 text-sm">Date Range:</label>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="form-select d-inline-block w-auto p-10 text-sm ml-10" 
          >
            <option value="last7">Last 7 Days</option>
            <option value="last30">Last 30 Days</option>
            <option value="custom">Custom</option>
          </select>

          {dateFilter === "custom" && (
            <>
              <DatePicker
                selected={customStartDate}
                onChange={(date) => setCustomStartDate(date)}
                selectsStart
                startDate={customStartDate}
                endDate={customEndDate}
                className="datepicker_custom_form-control text-sm"
                placeholderText="Start Date"
                calendarClassName="custom-datepicker"
                maxDate={new Date()}
              />
              <DatePicker
                selected={customEndDate}
                onChange={(date) => setCustomEndDate(date)}
                selectsEnd
                startDate={customStartDate}
                endDate={customEndDate}
                className="datepicker_custom_form-control text-sm"
                placeholderText="End Date"
                calendarClassName="custom-datepicker"
                maxDate={new Date()}
                minDate={customStartDate}
              />
            </>
          )}

          {/* <label className="mb-2 text-sm">Granularity:</label>
          <select
            value={step}
            onChange={(e) => setStep(e.target.value)}
            className="form-select d-inline-block w-auto"
          >
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
          </select> */}
        </div>
      </div>

      {/* Charts for Calls and Minutes */}
      <div className="row g-4">
        <div className="col-md-6">
          <div className="p-3 rounded shadow-sm bg-white">
            <h6 className="mb-2 fw-bold text-lg mb-0">Number of Calls</h6>
            <p className="mb-2 text-sm">How many calls were made per step.</p>
            <Line data={callChartData} options={{
              plugins: {
                legend: {
                  display: false
                }
              }
            }} />
          </div>
        </div>

        <div className="col-md-6">
          <div className="p-3 rounded shadow-sm bg-white">
            <h6 className="mb-2 fw-bold text-lg mb-0">Total Minutes</h6>
            <p className="mb-2 text-sm">Total call duration per step.</p>
            <Line data={minutesChartData} options={{
              plugins: {
                legend: {
                  display: false
                }
              }
            }} />
          </div>
        </div>
      </div>

      {/* Reasons Chart Full Width */}
      <div className="row mt-5">
        <div className="col-md-12">
          <div className="p-3 rounded shadow-sm bg-white">
            <h6 className="mb-2 fw-bold text-lg mb-0">Reasons Call Ended</h6>
            <p className="mb-2 text-sm">Breakdown of why calls were ended.</p>
            <div style={{ height: "300px" }}>
              <Bar
                data={reasonsChartData}
                options={options}
              />
            </div>

          </div>
        </div>

      </div>
    </div>


  );
};

export default UnitCountSeven;
